// TOEFL iBT Mock 8 — Listening (2026 format, multistage adaptive)
//
// Task types:
//   response     – Listen and Choose a Response: hear one line, pick the best reply.
//   conversation – Listen to a Conversation (campus / everyday life).
//   announcement – Listen to an Announcement (campus or public notice).
//   talk         – Listen to an Academic Talk (short lecture).
//
// Topics are unique to this mock. The harder Module 2 uses indirect replies,
// inversion ("No sooner had I…") and implied-meaning questions.
//
// Each recording plays once. Questions appear after it finishes.
// gender ("m" / "f") picks the synthesized voice for each line.

export const MODULE_SECONDS = { module1: 15 * 60, module2: 12 * 60 };

export const TASK_LABELS = {
  response: "Choose a Response",
  conversation: "Conversation",
  announcement: "Announcement",
  talk: "Academic Talk",
};

const W = (text) => ({ speaker: "Woman", gender: "f", text });
const M = (text) => ({ speaker: "Man", gender: "m", text });

const response = (id, line, options, correct) => ({
  id,
  type: "response",
  lines: [line],
  questions: [{ question: "Choose the best response.", options, correct }],
});

export const listeningModules = {
  module1: [
    response("m1-r1", W("Do you happen to know if the gym is open on Sunday?"), [
      "I happen to like the gym.",
      "I think it opens at noon on Sundays.",
      "Sunday was a busy day.",
      "The gym has new equipment.",
    ], 1),
    response("m1-r2", M("I'm thinking of joining the photography club."), [
      "You should. They go on great trips.",
      "I took a photo of the club.",
      "The club meets in a building.",
      "I joined a gym last year.",
    ], 0),
    response("m1-r3", W("Why don't we split the cost of the textbook?"), [
      "The textbook has twelve chapters.",
      "I split my notes into sections.",
      "That's a great way to save money.",
      "The bookstore closes at five.",
    ], 2),
    response("m1-r4", M("Hasn't the deadline for the scholarship already passed?"), [
      "The scholarship is for science students.",
      "I passed the exam yesterday.",
      "I applied for a scholarship.",
      "No, it was extended until next week.",
    ], 3),
    response("m1-r5", W("I can't seem to find my calculator anywhere."), [
      "Did you check your backpack?",
      "I calculated it twice.",
      "The calculator is expensive.",
      "I can find the answer.",
    ], 0),
    response("m1-r6", M("Would you rather meet in the morning or the afternoon?"), [
      "I met him in the morning.",
      "Afternoons are better for me.",
      "The meeting was long.",
      "I'd rather walk.",
    ], 1),
    response("m1-r7", W("The professor wants our drafts by Wednesday, not Friday."), [
      "Friday is my favorite day.",
      "I drafted a letter on Wednesday.",
      "Then I'd better start writing tonight.",
      "The professor teaches on Fridays.",
    ], 2),
    response("m1-r8", M("You look exhausted. Did you stay up late?"), [
      "I'm looking for my keys.",
      "I stayed at a hotel.",
      "Late is better than never.",
      "Yes, I was finishing my lab report.",
    ], 3),
    {
      id: "m1-conv",
      type: "conversation",
      title: "Returning a Textbook",
      lines: [
        W("Hi, I'd like to return this chemistry textbook. I bought it last week, but I just found out my professor changed the required edition."),
        M("Let me take a look. Do you have your receipt?"),
        W("Yes, here it is. I haven't written in it or anything."),
        M("Okay. Normally we give a full refund within fourteen days, and you're within that. But I see the plastic wrap has been removed, and the book came with an access code for the online homework."),
        W("Oh, I opened it, but I never used the code."),
        M("Unfortunately, once the wrap is off, we can't tell whether the code has been used, so we can only offer store credit, not cash."),
        W("Hmm. Well, I still need to buy the new edition. Can I use the credit for that?"),
        M("Absolutely. And the new edition is on the shelf right behind you, so we can do it all at once."),
      ],
      questions: [
        {
          question: "Why does the woman want to return the textbook?",
          options: [
            "It is damaged.",
            "Her professor requires a different edition.",
            "She dropped the chemistry course.",
            "She found it cheaper online.",
          ],
          correct: 1,
        },
        {
          question: "Why can't the woman get a cash refund?",
          options: [
            "She lost her receipt.",
            "It has been more than fourteen days.",
            "The store cannot confirm that the access code is unused.",
            "She wrote in the book.",
          ],
          correct: 2,
        },
        {
          question: "What will the woman probably do next?",
          options: [
            "Use the store credit to buy the new edition",
            "Ask her professor for a refund",
            "Come back later with her receipt",
            "Return the access code online",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m1-ann",
      type: "announcement",
      title: "Residence Hall Quiet Hours",
      lines: [
        W("Good evening, residents. Beginning this Sunday, quiet hours in all residence halls will be extended for the final exam period. Quiet hours will now run from eight p.m. to ten a.m. every day, instead of starting at the usual time of eleven p.m. During these hours, music, television and conversations should not be heard outside your room. The study lounges on each floor will be open twenty-four hours a day. Residents who repeatedly break the quiet hours policy may be asked to meet with their hall director. Extended quiet hours end the day after the last exam."),
      ],
      questions: [
        {
          question: "What is the main topic of the announcement?",
          options: [
            "New study lounges on each floor",
            "A change in exam dates",
            "Longer quiet hours during exams",
            "A ban on televisions",
          ],
          correct: 2,
        },
        {
          question: "What may happen to residents who keep breaking the policy?",
          options: [
            "They will lose access to the study lounges.",
            "They may have to meet with their hall director.",
            "They will be moved to another hall.",
            "They will have to pay a fine.",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "m1-talk",
      type: "talk",
      title: "Geology: How Limestone Caves Form",
      lines: [
        M("Many of the world's largest caves are found in limestone, and the reason lies in a surprisingly gentle chemical process. Rainwater absorbs a small amount of carbon dioxide from the air and, even more, from the soil it passes through. This turns the water into a very weak acid, carbonic acid. Limestone is made mostly of calcium carbonate, which dissolves slowly in this acid. As the water seeps down through cracks in the rock, it gradually widens them. Over hundreds of thousands of years, the cracks become channels, and the channels become passages large enough to walk through. Now, the same chemistry also works in reverse. When water dripping from a cave ceiling releases its carbon dioxide into the cave air, it can no longer hold all of the dissolved mineral, so a tiny amount is left behind. Drop by drop, these deposits build up into stalactites hanging from the ceiling and stalagmites rising from the floor. They grow incredibly slowly, often less than a centimeter per century, which is why cave visitors are asked never to touch them. The oils from human skin can stop the mineral from sticking, and a formation that took ten thousand years to grow can be ruined in a moment."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "How limestone caves and their formations develop",
            "Why caves are dangerous to explore",
            "How carbon dioxide affects the climate",
            "Different types of rock around the world",
          ],
          correct: 0,
        },
        {
          question: "According to the professor, what allows rainwater to dissolve limestone?",
          options: [
            "Minerals from the ocean",
            "Heat from inside the Earth",
            "Carbon dioxide it absorbs from the air and soil",
            "Oils from plants",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor say the chemistry \"works in reverse\"?",
          options: [
            "To show that caves can close up quickly",
            "To explain how stalactites and stalagmites form",
            "To explain why limestone is soft",
            "To describe how rainwater becomes acidic",
          ],
          correct: 1,
        },
        {
          question: "Why are visitors asked not to touch cave formations?",
          options: [
            "The formations are sharp.",
            "The formations are poisonous.",
            "Touching them releases carbon dioxide.",
            "Oils from skin can stop them from growing.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    response("up-r1", M("I'd have thought you'd be relieved the exam was postponed."), [
      "I'm relieved the exam is over.",
      "I would be, but now it clashes with my trip.",
      "I thought about postponing it.",
      "The exam was in the morning.",
    ], 1),
    response("up-r2", W("It's not as if the professor didn't warn us about the reading load."), [
      "True. I guess we have only ourselves to blame.",
      "The professor warned us about the weather.",
      "Reading is my favorite hobby.",
      "The load was very heavy to carry.",
    ], 0),
    response("up-r3", M("No sooner had I sat down than the fire alarm went off."), [
      "I sat down after the alarm.",
      "The fire was put out quickly.",
      "Did you have to go straight back outside?",
      "I'd sit down if I were you.",
    ], 2),
    response("up-r4", W("You might want to double-check those figures before you send the report."), [
      "I sent the figures last week.",
      "I double-checked my bag.",
      "The report has many figures in it.",
      "Why? Did you spot a mistake?",
    ], 3),
    response("up-r5", M("I could have sworn the library closed at ten."), [
      "It used to, but they changed the hours.",
      "I swore I would study there.",
      "The library has ten floors.",
      "I closed my laptop at ten.",
    ], 0),
    {
      id: "up-conv",
      type: "conversation",
      title: "Asking to Join a Full Seminar",
      lines: [
        M("Professor Haddad, I'm sorry to bother you. I tried to register for your seminar on migration and identity, but it's full. Is there any chance you could let me in?"),
        W("I appreciate your interest, Leo, but the seminar is capped at fifteen because it's discussion-based. With more than that, not everyone gets a chance to speak."),
        M("I understand. It's just that I need one more upper-level course to graduate in the spring, and this one fits my thesis topic perfectly."),
        W("Hmm. What's your thesis about?"),
        M("Second-generation immigrants and language loss. I've already interviewed about twenty families."),
        W("That's actually very relevant to week eight. Here's what I can do: usually one or two students drop out in the first week. Come to the first session anyway, and if a place opens up, it's yours."),
        M("That would be great. Should I do the readings for the first session?"),
        W("Please do. And if no one drops, talk to your advisor about an independent study. I'd be willing to supervise one, given your research."),
      ],
      questions: [
        {
          question: "Why does the professor limit the seminar to fifteen students?",
          options: [
            "The classroom is too small.",
            "The department sets the limit.",
            "She teaches several other courses.",
            "She wants every student to be able to take part in discussions.",
          ],
          correct: 3,
        },
        {
          question: "Why does the professor mention \"week eight\"?",
          options: [
            "To suggest that the student's research is connected to the course content",
            "To tell the student when a place will open",
            "To explain when the readings are due",
            "To show that the course is too advanced for him",
          ],
          correct: 0,
        },
        {
          question: "What does the professor offer if no one drops the course?",
          options: [
            "To raise the class limit",
            "To supervise an independent study",
            "To let him audit the class",
            "To recommend another seminar",
          ],
          correct: 1,
        },
      ],
    },
    {
      id: "up-talk",
      type: "talk",
      title: "History of Science: The Longitude Problem",
      lines: [
        W("For centuries, sailors could find their latitude, how far north or south they were, fairly easily, by measuring the angle of the sun or certain stars above the horizon. Longitude, their east-west position, was another matter entirely. The problem is fundamentally about time. Because the Earth rotates fifteen degrees every hour, if a navigator knew the exact time at a reference point, say the home port, and could compare it with local time worked out from the sun, the difference would reveal the ship's longitude. The obstacle was that no clock could keep accurate time at sea. Pendulum clocks, the most precise of the era, were useless on a rolling ship, and changes in temperature and humidity affected every mechanism. The consequences were severe. Ships routinely missed their destinations, and in 1707 a navigational error led four British warships onto rocks, killing well over a thousand sailors. In response, the British government offered an enormous prize for a practical solution. Most of the scientific establishment expected an astronomical method, using the moon's position against the stars. But the breakthrough came from John Harrison, a largely self-taught carpenter and clockmaker, who spent decades building marine clocks that compensated for motion and temperature. His fourth design lost only about five seconds on a voyage to Jamaica. Even so, the prize committee, which was dominated by astronomers, was reluctant to reward him in full, and he received most of the money only late in life, after the king himself intervened."),
      ],
      questions: [
        {
          question: "What is the talk mainly about?",
          options: [
            "Why finding longitude was difficult and how the problem was solved",
            "How sailors measured latitude using the stars",
            "The causes of a famous shipwreck",
            "The life of a British king",
          ],
          correct: 0,
        },
        {
          question: "Why does the professor explain that the Earth rotates fifteen degrees every hour?",
          options: [
            "To explain how latitude was measured",
            "To describe how pendulum clocks worked",
            "To show why accurate timekeeping was the key to finding longitude",
            "To explain why voyages to Jamaica took so long",
          ],
          correct: 2,
        },
        {
          question: "Why does the professor mention the shipwreck of 1707?",
          options: [
            "To show that warships used better clocks",
            "To illustrate how serious the problem was",
            "To explain why Harrison became a clockmaker",
            "To criticize the British navy's training",
          ],
          correct: 1,
        },
        {
          question: "What does the professor imply about the prize committee?",
          options: [
            "It rewarded Harrison as soon as his clock was tested.",
            "It was led mainly by clockmakers.",
            "It rejected all mechanical designs as too expensive.",
            "Its members may have favored an astronomical solution.",
          ],
          correct: 3,
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    response("low-r1", M("What time does the library close today?"), [
      "At nine tonight.",
      "It's a big library.",
      "I closed the door.",
      "Yesterday.",
    ], 0),
    response("low-r2", W("Would you like some tea?"), [
      "I like the color.",
      "Yes, please. With milk.",
      "It's teatime.",
      "I made it yesterday.",
    ], 1),
    response("low-r3", M("Where did you buy that jacket?"), [
      "It's very warm.",
      "Last winter.",
      "At the mall downtown.",
      "It's blue.",
    ], 2),
    response("low-r4", W("Are you ready for the test tomorrow?"), [
      "The test was hard.",
      "Tomorrow is Tuesday.",
      "I tested it.",
      "I think so. I studied all weekend.",
    ], 3),
    response("low-r5", M("Can I borrow a pen?"), [
      "Sure, here you go.",
      "I have a pen pal.",
      "It's a nice pen.",
      "I borrowed one yesterday.",
    ], 0),
    {
      id: "low-ann",
      type: "announcement",
      title: "Student Art Exhibition",
      lines: [
        M("Hello, everyone. The art department's student exhibition opens this Friday in the Main Gallery on the first floor of the Arts Building. You can see paintings, photographs and sculptures made by first-year students. The gallery is open from ten a.m. to five p.m., and entry is free. On Friday evening at six, there will be an opening party with music and snacks. Everyone is welcome."),
      ],
      questions: [
        {
          question: "What kind of event is being announced?",
          options: ["A music concert", "A student art exhibition", "An art class", "A photography contest"],
          correct: 1,
        },
        {
          question: "What will happen on Friday at six p.m.?",
          options: [
            "An opening party will be held.",
            "The gallery will close.",
            "Prizes will be given out.",
            "A painting class will begin.",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "low-talk",
      type: "talk",
      title: "Biology: Why Cats Purr",
      lines: [
        W("Most people think cats purr only when they are happy. It's true that cats often purr when someone pets them. But cats also purr when they are hurt, sick or scared. So why do they do it? Scientists believe that purring is a way for cats to communicate. Kittens purr to tell their mother that they are safe and warm. Some scientists also think purring may help cats heal. The vibrations of a purr are at a low frequency, and similar vibrations have been shown to help bones and muscles repair themselves."),
      ],
      questions: [
        {
          question: "According to the talk, when do cats purr?",
          options: [
            "Only when they are happy",
            "Only when they are hungry",
            "When they are happy and also when they are hurt or scared",
            "Only when they are kittens",
          ],
          correct: 2,
        },
        {
          question: "Why do kittens purr?",
          options: [
            "To tell their mother they are safe",
            "To ask for food",
            "To scare other animals",
            "To fall asleep",
          ],
          correct: 0,
        },
        {
          question: "What might the vibrations of purring help with?",
          options: ["Seeing in the dark", "Repairing bones and muscles", "Catching mice", "Staying warm"],
          correct: 1,
        },
      ],
    },
  ],
};
